/**
 * Helpers for grouping the resource-timeline scheduler by team.
 *
 * FullCalendar groups resources by a single `resourceGroupField` value, so a
 * user who belongs to multiple teams needs one duplicate row per team. Each row
 * carries a composite id encoding the user (and team), letting us map the id
 * back to a user id for drag/drop and slot selection.
 */

export const UNASSIGNED_RESOURCE_ID = 'unassigned';

/**
 * Unit separator (never present in a team name). Prefixes the group value with
 * a zero-padded order index so FullCalendar orders groups deterministically
 * while `resourceGroupLabelContent` renders the name. A trailing group id stays
 * stable as teams are added or renamed, unlike the order index or name.
 */
const GROUP_KEY_SEPARATOR = '\u001f';

// Order indexes for the two synthetic buckets. Unassigned sorts first (before
// real teams, which start at 1); No team sorts last.
const UNASSIGNED_ORDER_INDEX = 0;
const NO_TEAM_ORDER_INDEX = 9999;

const padOrderIndex = (index: number) => String(index).padStart(4, '0');

export const NO_TEAM_GROUP_ID = 'no-team';
export const UNASSIGNED_GROUP_ID = 'unassigned';

export const buildTeamGroupKey = (
  orderIndex: number,
  label: string,
  groupId: string
) => [padOrderIndex(orderIndex), label, groupId].join(GROUP_KEY_SEPARATOR);

export const NO_TEAM_GROUP_KEY = buildTeamGroupKey(
  NO_TEAM_ORDER_INDEX,
  'No team',
  NO_TEAM_GROUP_ID
);
export const UNASSIGNED_GROUP_KEY = buildTeamGroupKey(
  UNASSIGNED_ORDER_INDEX,
  'Unassigned',
  UNASSIGNED_GROUP_ID
);

/** The team name to render as a group divider label. */
export const teamGroupKeyToLabel = (groupKey: string) =>
  groupKey.split(GROUP_KEY_SEPARATOR)[1] ?? groupKey;

/** The team id, or `NO_TEAM_GROUP_ID` / `UNASSIGNED_GROUP_ID`. */
export const teamGroupKeyToGroupId = (groupKey: string) =>
  groupKey.split(GROUP_KEY_SEPARATOR)[2] ?? groupKey;

/**
 * Drop selected team ids for teams that no longer exist (e.g. deleted while
 * still persisted in the filter) so a stale id can't blank the whole schedule.
 * Pass the teams currently known to the account; an empty result means the
 * filter should be treated as inactive.
 */
export const pruneStaleTeamIds = (
  selectedTeamIds: string[],
  teams: { id: string }[]
): string[] => {
  const knownIds = new Set(teams.map((team) => team.id));
  return selectedTeamIds.filter((id) => knownIds.has(id));
};

/** Composite resource id for a (user, team) row. `null` team -> the No-team row. */
export const buildTeamResourceId = (
  userId: number,
  teamId: string | null
): string => `u${userId}__${teamId === null ? 'no-team' : `t${teamId}`}`;

/**
 * Extract the user id from a resource id, working for both grouped composite
 * ids (`u123__t...`) and the flat mode's bare numeric id (`123`). Returns
 * `undefined` for the unassigned row / unparseable ids.
 */
export const resourceIdToUserId = (
  resourceId: string | number | undefined | null
): number | undefined => {
  if (resourceId === undefined || resourceId === null) return undefined;
  const id = String(resourceId);
  if (id === UNASSIGNED_RESOURCE_ID) return undefined;
  const composite = id.match(/^u(\d+)__/);
  if (composite) return +composite[1];
  return /^\d+$/.test(id) ? +id : undefined;
};
